import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import LoginLoading from "./LoginLoading";
import { clearRememberedCredentials, loadRememberedCredentials, login, saveRememberedCredentials } from "../../services/auth";
import RIcon from "../../components/RIcon";

export default function LoginPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "", remember: false });
  const [loading, setLoading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    loadRememberedCredentials()
      .then(({ username, password }) => {
        if (cancelled) return;
        setForm({
          username,
          password,
          remember: !!(username || password),
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.username.trim()) errs.username = "请输入用户名或邮箱";
    if (!form.password.trim()) errs.password = "请输入密码";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);

    try {
      await login(form.username, form.password, form.remember);
      if (form.remember) {
        await saveRememberedCredentials(form.username, form.password);
      } else {
        clearRememberedCredentials();
      }
      setLoading(false);
      setShowOverlay(true);
    } catch (err) {
      setLoading(false);
      setErrors({ password: err instanceof Error ? err.message : "登录失败" });
    }
  };

  return (
    <>
      {showOverlay && (
        <LoginLoading onDone={() => navigate("/workbench/ai-chat")} />
      )}

      <div className="min-h-[100dvh] flex overflow-y-auto">
        {/* Left Brand Panel */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20medical%20pharmaceutical%20technology%20background%20with%20teal%20cyan%20blue%20green%20geometric%20patterns%20molecular%20structures%20scientific%20innovation%20modern%20minimal%20design%20professional%20atmosphere&width=960&height=1080&seq=login-bg-001&orientation=portrait"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A4A47]/85 to-[#0EA5A0]/60" />
          <div className="relative z-10 flex flex-col justify-between p-12 w-full">
            <div className="flex items-center gap-3">
              <img
                src="https://public.readdy.ai/ai/img_res/a84828b1-10f3-4f8c-851b-b2f4fe56a73c.png"
                alt="协和医疗"
                className="h-9 w-auto brightness-0 invert"
              />
              <span className="text-white font-semibold text-base">协和医疗全域AI系统</span>
            </div>

            <div>
              <h1 className="text-4xl font-extrabold text-white leading-tight mb-4">
                智慧知识，
                <br />驱动医疗创新
              </h1>
              <p className="text-white/70 text-base leading-relaxed max-w-sm">
                统一知识库 · AI智能问答 · 智能文档生成，为医疗制药企业打造专业的知识管理平台
              </p>
              <div className="flex flex-wrap gap-2 mt-6">
                {["GMP合规", "ICH标准", "数据安全", "权限管控"].map((tag) => (
                  <span key={tag} className="bg-white/15 border border-white/20 text-white text-xs px-3 py-1.5 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* Right Form Panel */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 bg-white overflow-y-auto">
          <div className="w-full max-w-sm py-6">
            <div className="flex items-center gap-2 mb-8 lg:hidden">
              <img src="https://public.readdy.ai/ai/img_res/a84828b1-10f3-4f8c-851b-b2f4fe56a73c.png" alt="协和医疗" className="h-7 w-auto" />
              <span className="text-[#1A2B3C] font-semibold text-sm">协和医疗全域AI系统</span>
            </div>

            <h2 className="text-2xl font-bold text-[#1A2B3C] mb-1">欢迎回来</h2>
            <p className="text-[#718096] text-sm mb-8">登录您的内部账号以继续</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">用户名 / 邮箱</label>
                <div className={`flex items-center border rounded-lg px-3 h-11 gap-2 transition-colors ${
                  errors.username ? "border-red-400 bg-red-50" : "border-gray-200 focus-within:border-[#0EA5A0]"
                }`}>
                  <RIcon name="user-line" className="text-[#718096] text-sm" />
                  <input
                    type="text"
                    placeholder="请输入用户名或邮箱"
                    className="flex-1 bg-transparent outline-none text-[16px] md:text-sm text-[#1A2B3C] placeholder:text-gray-400"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                  />
                </div>
                {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">密码</label>
                <div className={`flex items-center border rounded-lg px-3 h-11 gap-2 transition-colors ${
                  errors.password ? "border-red-400 bg-red-50" : "border-gray-200 focus-within:border-[#0EA5A0]"
                }`}>
                  <RIcon name="lock-line" className="text-[#718096] text-sm" />
                  <input
                    type="password"
                    placeholder="请输入密码"
                    className="flex-1 bg-transparent outline-none text-[16px] md:text-sm text-[#1A2B3C] placeholder:text-gray-400"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </div>
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.remember}
                    onChange={(e) => setForm({ ...form, remember: e.target.checked })}
                    className="w-4 h-4 rounded accent-[#0EA5A0]"
                  />
                  <span className="text-sm text-[#4A5568]">记住我</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-[#0EA5A0] hover:underline">
                  忘记密码？
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-70 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap relative overflow-hidden group"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 pointer-events-none" />
                {loading ? (
                  <>
                    <RIcon name="loader-4-line" className="animate-spin" />
                    验证中...
                  </>
                ) : (
                  <>
                    <RIcon name="login-box-line" className="text-base" />
                    登 录
                  </>
                )}
              </button>
            </form>

            <p className="text-center text-sm text-[#718096] mt-6">
              没有账号？
              <Link to="/register" className="text-[#0EA5A0] font-medium hover:underline ml-1">
                立即注册
              </Link>
            </p>

            <p className="text-center text-xs text-gray-300 mt-8">
              © 2026 协和医疗 · 仅限授权人员访问
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
